import { parseUrlWithSite } from '@seek/melways-router-utils';
import {
  type AlternativeName,
  getSiteNameFromHostname,
  isMelwaysSiteName,
  type SiteName,
} from '@seek/melways-sites';

import type { Flatten } from './flatten-types';

export type MelwaysInformation = Flatten<
  ReturnType<typeof parseUrlWithSite> & { alternative?: AlternativeName }
>;

type SiteOrHostName = { site: SiteName } | { hostname: string };

/**
 * Creates Melways information similar to what would be returned from useMelwaysInfo.
 * Useful for retrieving Melways information outside of React context such as server-side code before starting to render.
 *
 * Caution: This method should not be used in-place of useMelwaysInfo. Consider using useMelwaysInfo inside of a React application.
 *
 * Pass either a site or host. Trailing '.local' will be ignored when using host.
 *
 * @example
 * import { MelwaysProvider, getMelwaysInfo } from '@seek/melways-react';
 *
 * const melwaysInfo = getMelwaysInfo({
 *   path: '/jobs',
 *   site: 'candidate-seek-au',
 * });
 *
 * <MelwaysProvider.Server {...melwaysInfo}>
 *   {" your app "}
 * </MelwaysProvider.Server>
 */
export function getMelwaysInfo(params: {
  path: string | URL;
  site: SiteName;
  staging?: boolean;
  alternative?: AlternativeName;
}): MelwaysInformation;
export function getMelwaysInfo(params: {
  path: string | URL;
  hostname: string;
  staging?: boolean;
  alternative?: AlternativeName;
}): MelwaysInformation;
export function getMelwaysInfo(
  params: {
    path: string | URL;
    staging?: boolean;
    alternative?: AlternativeName;
  } & SiteOrHostName,
): MelwaysInformation {
  const { path, staging, alternative } = params;

  let site: SiteName;
  if ('site' in params) {
    site = params.site;
  } else {
    const normalisedHost = params.hostname.replace(/\.local$/, '');
    site = getSiteNameFromHostname(normalisedHost);
  }

  if (!isMelwaysSiteName(site)) {
    if ('hostname' in params) {
      throw new Error(
        `Melways was unable to identify valid site from host. Recieved: ${params.hostname}`,
      );
    }
    throw new Error(
      `Melways recieved invalid site name. Recieved: ${params.site}`,
    );
  }

  const parsedContext = parseUrlWithSite({
    url: path,
    site,
    staging,
  });

  return {
    ...parsedContext,
    ...(alternative ? { alternative } : {}),
  };
}
